/**
 * Media-library React Query hooks.
 *
 * Wraps the `/media` endpoints (list, upload, metadata patch, soft-delete).
 * Keys live under ['media', ...].
 *
 * The multipart upload goes through `fetch` directly: the shared `useApi`
 * client hard-codes `Content-Type: application/json`, which strips the
 * multipart boundary and makes `express.json()` reject the body with a 400
 * ("Unexpected token '-', \"------WebK\"... is not valid JSON"). Same
 * workaround as `features/projects/api/attachmentsHooks.ts`.
 */
import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthState } from '@revalize/auth/frontend'
import { useApi } from '@/hooks/useAppApi'

export type MediaCategory =
  | 'tenant_logo'
  | 'product_image'
  | 'project_attachment'
  | 'avatar'
  | 'other'

export interface MediaItem {
  id: string
  tenantId: string
  ownerUserId: string | null
  objectKey: string
  contentType: string
  sizeBytes: number
  category: MediaCategory
  label: string
  tags: string[]
  scanStatus: 'pending' | 'clean' | 'infected'
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface MediaListResponse {
  items: MediaItem[]
  total: number
}

export interface MediaListParams {
  category?: MediaCategory
  q?: string
  tag?: string
  limit?: number
  offset?: number
}

export function useMediaList(params: MediaListParams = {}) {
  const api = useApi()
  const qs = new URLSearchParams()
  if (params.category) qs.set('category', params.category)
  if (params.q) qs.set('q', params.q)
  if (params.tag) qs.set('tag', params.tag)
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.offset) qs.set('offset', String(params.offset))
  const query = qs.toString()
  return useQuery<MediaListResponse>({
    queryKey: ['media', 'list', query],
    queryFn: () =>
      api.get(`/media${query ? '?' + query : ''}`) as Promise<MediaListResponse>,
  })
}

/** Storage routing kind — picks the destination container on the backend. */
export type MediaStorageKind = 'image' | 'cad' | 'doc'

export interface MediaUploadInput {
  file: File
  label?: string
  category?: MediaCategory
  tags?: string[]
  /**
   * Routes the upload to a dedicated, CDN-addressable container:
   *   'image' → media-assets, 'cad' → revcad-assets, 'doc' → doclink-thumbnails.
   * Omit for user-scoped storage (default).
   */
  kind?: MediaStorageKind
}

/**
 * The API wraps JSON responses in `{ success, data }`. The shared `useApi`
 * client unwraps that for us; a raw `fetch` does not, so do it here — otherwise
 * callers read `row.id` off the envelope and get `undefined`. Tolerates a bare
 * (unwrapped) body too.
 */
function unwrapEnvelope<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in body && 'success' in body) {
    return (body as { data: T }).data
  }
  return body as T
}

/** Pull the server's error message out of either shape. */
function errorMessageFrom(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as
      | { error?: string }
      | { data?: { error?: string } }
    const direct = (parsed as { error?: string }).error
    if (direct) return direct
    const nested = (parsed as { data?: { error?: string } }).data?.error
    if (nested) return nested
  } catch {
    // Non-JSON body — fall through to the caller's generic message.
  }
  return null
}

/** POST a media file as real `multipart/form-data` (browser sets the boundary). */
async function uploadMediaMultipart(token: string, input: MediaUploadInput): Promise<MediaItem> {
  const form = new FormData()
  form.append('file', input.file)
  if (input.label) form.append('label', input.label)
  if (input.category) form.append('category', input.category)
  if (input.tags) form.append('tags', input.tags.join(','))
  if (input.kind) form.append('kind', input.kind)

  // Relative on purpose: the API is same-origin behind `/api`
  // (`ConfigService.getApiBaseUrl()` is hard-coded to it, and browser code is
  // documented to use relative URLs).
  const res = await fetch(`${API_BASE_URL}/media`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    const serverMessage = errorMessageFrom(body)
    if (serverMessage) throw new Error(serverMessage)
    const suffix = body ? `: ${body.slice(0, 200)}` : ''
    throw new Error(`Upload failed (${res.status})${suffix}`)
  }
  const row = unwrapEnvelope<MediaItem>(await res.json())
  if (!row?.id) {
    throw new Error('Upload succeeded but the server returned no media id')
  }
  return row
}

/**
 * Base URL for token exchange. `useAuthState()` with no options cannot mint an
 * app token ("Base URL is required for token exchange"); the shared `useApi`
 * instance is built with `ConfigService.getApiBaseUrl()`, which is hard-coded to
 * this same value. Inlined rather than imported because `ConfigService` reaches
 * `import.meta`, which ts-jest's CJS transform cannot parse.
 */
const API_BASE_URL = '/api'

/**
 * Imperative uploader for callers that upload outside a mutation lifecycle —
 * e.g. the product editor, which defers uploads until the user clicks Save.
 */
export function useUploadMedia(): (input: MediaUploadInput) => Promise<MediaItem> {
  const authState = useAuthState({ baseUrl: API_BASE_URL })
  return useCallback(
    async (input: MediaUploadInput) => {
      const token = await authState.getAppToken()
      return uploadMediaMultipart(token, input)
    },
    [authState],
  )
}

export function useMediaMutations() {
  const api = useApi()
  const authState = useAuthState({ baseUrl: API_BASE_URL })
  const qc = useQueryClient()
  return {
    upload: useMutation({
      mutationFn: async (input: MediaUploadInput) => {
        const token = await authState.getAppToken()
        return uploadMediaMultipart(token, input)
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: ['media'] }),
    }),
    updateMeta: useMutation({
      mutationFn: ({
        id,
        patch,
      }: {
        id: string
        patch: { label?: string; tags?: string[]; category?: MediaCategory }
      }) => api.patch(`/media/${id}`, patch),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['media'] }),
    }),
    softDelete: useMutation({
      mutationFn: ({ id }: { id: string }) => api.delete(`/media/${id}`),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['media'] }),
    }),
  }
}
